import React from 'react';
import diseaseBaseLink from '../../../../services/GenerateLinkForIdentifier';
import noSentenceSupportingAssoc from '../../../../services/NoSentenceForAssociation';
import SentenceHtmlModal from '../../components/SentenceHtmlModal';
import { renderDefaultValueCols } from '@/utils/renderDefaultValueCols';

function arrangeColumns(
  index,
  columnName,
  isFilteringOrSorting,
  isColumnVisible,
  names,
  Columns,
  openFromMenu,
  tableIdent = '',
) {
  let tableColumns = Columns[index].columns.slice();

  // this fires only if the number of filters is greater than one and the tableColumns are NOT recreated
  // by the users actions (AKA: filtering or sorting a column and opening the external menu), with the exception
  // of making a new search
  if (names && names.length > 1 && columnName === '' && !openFromMenu && !isFilteringOrSorting) {
    tableColumns[0].visible = true;
    if (tableIdent === 'DDA') {
      tableColumns[1].visible = true;
      tableColumns[2].visible = true;
    }
  }

  // this fires only if the number of filters is exactly one and the tableColumns are NOT recreated
  // by the users actions (AKA: filtering or sorting a column and opening the external menu), with the exception
  // of erasing filters from the table
  if (names && names.length === 1 && columnName === '' && !openFromMenu && !isFilteringOrSorting) {
    if (tableIdent === 'CHEMICALS') tableColumns[0].visible = true;
    else tableColumns[0].visible = false;

    if (tableIdent === 'DDA') {
      tableColumns[1].visible = false;
      tableColumns[2].visible = false;
    }
  }

  // change the visible state of the chosen column
  if (columnName !== '') {
    tableColumns.forEach(column => {
      if (column.name === columnName) {
        column.visible = isColumnVisible;
      }
    });
  }

  return tableColumns;
}

function renderJaccard(instance, type) {
  const jcrdType = type === 'GENE' ? 'jaccard_genes' : 'jaccard_variants';
  let jcrd = instance.data[jcrdType] || null;
  if (jcrd != null) {
    if (jcrd < 0.01) {
      return jcrd?.toExponential(2);
    } else {
      return jcrd?.toFixed(2);
    }
  } else {
    return null;
  }
}

function renderAlleleDataVDA(instance, alleleType) {
  const alleleFreq = instance?.data?.[alleleType];

  if (!Array.isArray(alleleFreq) || alleleFreq.length === 0) {
    return renderDefaultValueCols(null);
  }

  const formattedList = alleleFreq.map(el => {
    if (el === -1 || el === null || el === undefined) {
      return renderDefaultValueCols(null);
    }

    if (el < 0.01) {
      return el.toExponential(2);
    }

    return el.toFixed(2);
  });

  // Si todos son '-'
  if (!formattedList.some(val => val !== '-')) {
    return renderDefaultValueCols(null);
  }

  return formattedList.join('; ');
}


function renderAlleleDataVariant(instance, alleleType) {
  let alleles = instance.data.alleles;
  let allelicFrequencies = instance.data.frequencies;

  // Validate that alleles and frequencies exist and are arrays
  if (!alleles || !Array.isArray(alleles) || !allelicFrequencies || !Array.isArray(allelicFrequencies)) {
    return renderDefaultValueCols(null);
  }

  let results = [];

  // We don't always have AF for all the alternatives alleles, so cross reference the two fields for the order of the values.
  for (let allele of alleles) {
    let result = null;
    for (let freq of allelicFrequencies) {
      if (freq.alt === allele.alt) {
        if (freq.type === alleleType) {
          const alleleFreqValue = freq.AF;
          if (alleleFreqValue === -1) {
            result = null;
          } else if (alleleFreqValue < 0.01) {
            result = alleleFreqValue?.toExponential(2);
          } else {
            result = alleleFreqValue?.toFixed(2);
          }
        }
      }
    }
    results.push(result);
  }
  if (!results.some(val => val !== null)) {
    return renderDefaultValueCols(null);
  }

  const formattedResults = results.map(val =>
    renderDefaultValueCols(val)
  );

  return formattedResults.join('; ');
}

function renderVariantLink(instance) {
  const baseUrl = 'https://www.ncbi.nlm.nih.gov/snp/';
  const value = renderDefaultValueCols(instance?.data?.variantStrID);
  if (value === '-') return '-';

  return (
    <a href={`${baseUrl}${value}`} target="_blank" rel="noreferrer">
      {value} <i className="fa fa-external-link" aria-hidden="true"></i>
    </a>
  );
}

function renderDiseaseNameDDA(instance, DDADiseaseIdent) {
  const value = renderDefaultValueCols(instance?.data?.[`disease${DDADiseaseIdent}_Name`]);
  if (value === '-') {
    return '-';
  }

  const link = diseaseBaseLink(
    instance.data[`disease${DDADiseaseIdent}_UMLSCUI`]
  );

  return (
    <a href={link} title={value} target="_blank" rel="noreferrer">
      {value} <i className="fa fa-external-link" aria-hidden="true"></i>
    </a>
  );
}


function renderSentenceHTML(instance) {
  if (instance.data.sentenceHTML) {
    return <SentenceHtmlModal content={instance.data.sentenceHTML} allowCopy>{instance.data.sentenceHTML}</SentenceHtmlModal>;
  } else {
    return noSentenceSupportingAssoc();
  }
}

function renderRoundedValues(instance, attribute) {
  const result = renderDefaultValueCols(instance?.data?.[attribute]);
  if (result === '-' || result === '') {
    return '0.00';
  }
  if (typeof result === 'number') {
    return result.toFixed(2);
  }
  return result;
}

function renderDiseaseClassesAttributes(value: any) {
  const result = renderDefaultValueCols(value);
  if (result === '-' || !Array.isArray(result) || result.length === 0) {
    return '-';
  }

  const transformedValue = result.join('|');

  return (
    <span title={transformedValue}>
      {transformedValue}
    </span>
  );
}

function renderNCTid(instance) {
  const nctIdArray = instance.data.nct_id;
  if (nctIdArray.length !== 0) {
    const baseUrl = 'https://clinicaltrials.gov/search?id=';

    return (
      <span>
        {nctIdArray.map((id, index) => {
          const link = `${baseUrl}${id}`;
          return (
            <span key={id}>
              <a href={link} target="_blank" rel="noreferrer">
                {id} <i className="fa fa-external-link" aria-hidden="true"></i>
              </a>
              {index < nctIdArray.length - 1 && ' | '}
            </span>
          );
        })}
      </span>
    );
  } else return '';
}

function renderDecimalValues(value) {
  if (value === null) return null;
  if (value >= 0.01) return value?.toFixed(2);
  if (value !== 0 && value < 0.01) return value?.toExponential(2);
  if (value === 0) return 0;
}

function renderListsFromObjects(instance, instanceAttribute, attribute) {
  const list = renderDefaultValueCols(instance?.data?.[instanceAttribute]);
  if (list === '-' || !Array.isArray(list) || list.length === 0)
    return '-';

  const items = list
    .map(item => renderDefaultValueCols(item?.[attribute]))
    .filter(value => value !== '-');

  if (items.length === 0) return '-';

  return <span>{items.join(' | ')}</span>;
}

export {
  arrangeColumns,
  renderAlleleDataVDA,
  renderAlleleDataVariant,
  renderVariantLink,
  renderDiseaseNameDDA,
  renderSentenceHTML,
  renderRoundedValues,
  renderDiseaseClassesAttributes,
  renderNCTid,
  renderDecimalValues,
  renderListsFromObjects,
  renderJaccard,
};